import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress"; //Progress bar
import Snackbar from "@mui/material/Snackbar";

import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import DownloadIcon from "@mui/icons-material/CloudDownload";
import { IconButton, Menu, MenuItem } from "@mui/material";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as bookServices from "../services/book.services";
import DrawingLayer from "./DrawingLayer";
import "./PDFViewer.css"; // Import your CSS file
import SearchComponent from "./SearchComponent";
import VersionDropdown from "./VersionDropdown";
import ActionBar from "./actionBar/ActionBar";
import DigitalSidebar from "./digitalSidebar";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

const PDFViewer = ({ pdfURL, propPageNumber, mode, bookId, sample, setPageNumber: parentSetPageNumber, onFullscreenChange, fullscreenSidebar = true, isPDFCached = false, onDownloadProgress, isCachingInBackground = false, cachingProgress = 0 }) => {
  const [pageNumber, setLocalPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [viewMode, setViewMode] = useState("single"); // Dropdown state for viewing mode
  const [scale, setScale] = useState(1);
  const [isCached, setIsCached] = useState(isPDFCached);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pdfContainerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [annotations, setAnnotations] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]); // For search results
  const [currentMatch, setCurrentMatch] = useState(0); // For current match index
  const [totalMatches, setTotalMatches] = useState(0); // Total matches found
  const [highlightedMatches, setHighlightedMatches] = useState([]);
  const [encodedPdfURL, setEncodedPdfURL] = useState(null);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTool, setActiveTool] = useState("select");
  const [book, setBook] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarOpenMedia, setSnackbarOpenMedia] = useState(false);
  const [snackbarOpenMediaAndPdf, setSnackbarOpenMediaAndPdf] = useState(false);

  const [isMediaCached, setIsMediaCached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // New state for tracking total media files and caching progress
  const [totalMediaFiles, setTotalMediaFiles] = useState(0);
  const [cachedMediaFiles, setCachedMediaFiles] = useState(0);
  const [isMediaCaching, setIsMediaCaching] = useState(false);
  const [cachingFileName, setCachingFileName] = useState("");
  const [hasMedia, setHasMedia] = useState(false);
  const [mediaCachingProgress, setMediaCachingProgress] = useState(0);
  const [hasPDF, setHasPDF] = useState(!!pdfURL);
  
  // Reset download button states
  const [showDownloadButton, setShowDownloadButton] = useState(true);
  const [downloadButtonState, setDownloadButtonState] = useState({
    disabled: false,
    text: "Download for offline use"
  });

  const verifyCachedResource = async (url, expectedTypePrefix = null) => {
    // P0: search across the split cache buckets (pdf-cache-v1, media-cache-v1)
    const CACHE_BUCKETS = ['pdf-cache-v1', 'media-cache-v1', 'app-shell-v1'];
    try {
      for (const bucketName of CACHE_BUCKETS) {
        const cache = await caches.open(bucketName);
        const resp = await cache.match(url);
        if (!resp) continue;
        if (resp.type === 'opaque') return true; // Opaque responses exist in cache but cannot be read/blobbed
        const blob = await resp.clone().blob();
        if (!blob || blob.size === 0) continue;
        if (expectedTypePrefix) {
          const headerType = resp.headers.get("Content-Type");
          const blobType = blob.type;
          const effectiveType = headerType || blobType || "";
          if (!effectiveType.startsWith(expectedTypePrefix)) continue;
        }
        return true; // found a valid match
      }
      return false;
    } catch (e) {
      console.error("verifyCachedResource error", url, e);
      return false;
    }
  };

  // Create a function that updates both local and parent state
  const updatePageNumber = (newPageNumber) => {
    setLocalPageNumber(newPageNumber);
    if (parentSetPageNumber) {
      parentSetPageNumber(newPageNumber);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event) => {
    // Don't open the menu if everything is cached
    if ((isCached && isMediaCached) || 
        (isCached && !hasMedia) || 
        (!hasPDF && isMediaCached)) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!bookId || bookId === "null" || bookId === "undefined") {
      return;
    }

    //setIsLoading(true);
    bookServices
      .getBookById(bookId)
      .then((response) => {
        if (!response || !response.data) return;
        setBook(response.data);
        
        // Check if the book has media files
        if (response.data && response.data.pages) {
          const mediaExists = response.data.pages.some(page => 
            (page.voiceFiles && page.voiceFiles.length > 0) || 
            (page.videoFiles && page.videoFiles.length > 0)
          );
          setHasMedia(mediaExists);
          
          // Count total media files for progress tracking
          let totalMedia = 0;
          response.data.pages.forEach(page => {
            if (page.voiceFiles) totalMedia += page.voiceFiles.length;
            if (page.videoFiles) totalMedia += page.videoFiles.length;
          });
          setTotalMediaFiles(totalMedia);
        }
        
        // Check if the book has a PDF
        setHasPDF(!!pdfURL);
        
        // Update download button state based on caching status
        updateDownloadButtonState();
        
        //setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        //setIsLoading(false);
      });
  }, [bookId, pdfURL]);

  // Check if media files are cached
  useEffect(() => {
    const checkMediaCache = async () => {
      if (!book || !book.pages || !hasMedia) return;
      
      try {
        const mediaChecks = [];
        
        // Check all audio files
        book.pages.forEach(page => {
          if (page.voiceFiles) {
            page.voiceFiles.forEach(file => {
              const url = file.url.replace(
                "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
                "https://dng2cduv9v2yj.cloudfront.net"
              );
              mediaChecks.push(verifyCachedResource(url, "audio"));
            });
          }
          
          // Check all video files
          if (page.videoFiles) {
            page.videoFiles.forEach(file => {
              const url = file.url.replace(
                "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
                "https://dng2cduv9v2yj.cloudfront.net"
              );
              mediaChecks.push(verifyCachedResource(url, "video"));
            });
          }
        });
        
        const mediaResults = await Promise.all(mediaChecks);
        const cachedCount = mediaResults.filter(Boolean).length;
        
        setCachedMediaFiles(cachedCount);
        setIsMediaCached(cachedCount > 0 && cachedCount === totalMediaFiles);
        
        // Update download button state
        updateDownloadButtonState();
      } catch (error) {
        console.error("Error checking media cache:", error);
      }
    };
    
    checkMediaCache();
  }, [book, hasMedia, totalMediaFiles]);

  // Update download button state based on caching status
  const updateDownloadButtonState = () => {
    if (!hasPDF && !hasMedia) {
      // No content to download
      setDownloadButtonState({
        disabled: true,
        text: "Nothing to download"
      });
      return;
    }
    
    if (hasPDF && hasMedia) {
      // Both PDF and media exist
      if (isCached && isMediaCached) {
        setDownloadButtonState({
          disabled: true,
          text: "All content available offline"
        });
      } else if (isCached && !isMediaCached) {
        setDownloadButtonState({
          disabled: false,
          text: "Download media files"
        });
      } else if (!isCached && isMediaCached) {
        setDownloadButtonState({
          disabled: false,
          text: "Download PDF"
        });
      } else {
        setDownloadButtonState({
          disabled: false,
          text: "Download for offline use"
        });
      }
    } else if (hasPDF && !hasMedia) {
      // Only PDF exists
      if (isCached) {
        setDownloadButtonState({
          disabled: true,
          text: "PDF available offline"
        });
      } else {
        setDownloadButtonState({
          disabled: false,
          text: "Download PDF"
        });
      }
    } else if (!hasPDF && hasMedia) {
      // Only media exists
      if (isMediaCached) {
        setDownloadButtonState({
          disabled: true,
          text: "Media available offline"
        });
      } else {
        setDownloadButtonState({
          disabled: false,
          text: "Download media files"
        });
      }
    }
  };

  // Enhanced cachePDF function with progress tracking and verification
  const cachePDF = async () => {
    if (!pdfURL || isCached) return;
    
    setIsDownloading(true);
    setProgress(0);
    console.log("PDF download started...");
    
    try {
      const response = await fetch(pdfURL);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      
      const contentLength = +response.headers.get("Content-Length");
      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        // Calculate and update progress
        const progressPercentage = Math.floor((receivedLength / contentLength) * 100);
        setProgress(progressPercentage);
        
        // Call the progress callback if provided
        if (onDownloadProgress) {
          onDownloadProgress(progressPercentage);
        }
      }
      
      const cache = await caches.open("pdf-cache-v1"); // P0: was "appV1"
      const blob = new Blob(chunks);
      const responseBlob = new Response(blob, {
        headers: {
          "Content-Type": response.headers.get("Content-Type") || "application/pdf"
        }
      });
      
      await cache.put(pdfURL, responseBlob);

      const verified = await verifyCachedResource(pdfURL, "application/pdf");
      if (verified) {
        setIsCached(true);
        updateDownloadButtonState();
        setSnackbarOpen(true);
      } else {
        console.warn("PDF cached but verification failed");
      }
    } catch (error) {
      console.error("Error caching PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Function to cache media files (audio and video) with verification
  const cacheMediaFiles = async () => {
    if (!book || !book.pages || !hasMedia) return;
    
    setIsMediaCaching(true);
    setCachedMediaFiles(0);
    setMediaCachingProgress(0);
    
    try {
      const cache = await caches.open("media-cache-v1"); // P0: was "appV1"
      let processed = 0;
      let mediaFiles = [];
      
      // Collect all media files
      book.pages.forEach(page => {
        if (page.voiceFiles) {
          page.voiceFiles.forEach(file => {
            const cloudfrontUrl = file.url.replace(
              "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
              "https://dng2cduv9v2yj.cloudfront.net"
            );
            mediaFiles.push({
              url: cloudfrontUrl,
              name: file.name || decodeURIComponent(cloudfrontUrl.split('/').pop()),
              type: 'audio'
            });
          });
        }
        
        if (page.videoFiles) {
          page.videoFiles.forEach(file => {
            const cloudfrontUrl = file.url.replace(
              "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
              "https://dng2cduv9v2yj.cloudfront.net"
            );
            mediaFiles.push({
              url: cloudfrontUrl,
              name: file.name || decodeURIComponent(cloudfrontUrl.split('/').pop()),
              type: 'video'
            });
          });
        }
      });
      
      // Process files one by one
      for (const file of mediaFiles) {
        try {
          setCachingFileName(file.name);
          
          // Tell the service worker we want this media file (for range-awareness)
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              action: "requestMedia",
              url: file.url
            });
          }
          
          // Fetch CloudFront media with no-cors to bypass CORS blocks (returns opaque response)
          const response = await fetch(file.url, { mode: 'no-cors' });
          await cache.put(file.url, response);
          
          const verified = await verifyCachedResource(file.url, file.type);
          if (verified) {
            processed++;
            setCachedMediaFiles(processed);
            setMediaCachingProgress(Math.floor((processed / mediaFiles.length) * 100));
          } else {
            console.warn("Verification failed for:", file.url);
          }
          
        } catch (error) {
          console.error(`Error caching ${file.type} file:`, error);
        }
      }
      
      const allVerified = processed === mediaFiles.length;
      setIsMediaCached(allVerified);
      if (allVerified) {
        setSnackbarOpenMedia(true);
      }
      
      // Update download button state
      updateDownloadButtonState();
      
    } catch (error) {
      console.error("Error during media caching:", error);
    } finally {
      setIsMediaCaching(false);
    }
  };
  
  // Function to cache both PDF and media
  const cacheMediaAndPDF = async () => {
    // If PDF is not cached, cache it first
    if (hasPDF && !isCached) {
      await cachePDF();
    }
    
    // Then cache media files if needed
    if (hasMedia && !isMediaCached) {
      await cacheMediaFiles();
    }
    
    // Only show success if both verified
    if ((hasPDF ? isCached : true) && (hasMedia ? isMediaCached : true)) {
      setSnackbarOpenMediaAndPdf(true);
    }
  };

  // Handle annotation update from DrawingLayer
  const handleUpdateAnnotations = (newAnnotations) => {
    setAnnotations(newAnnotations);
  };
  // Load version from VersionDropdown
  const handleLoadVersion = (versionAnnotations) => {
    setAnnotations(versionAnnotations); // Update with loaded version
  };

  const handleContextMenu = (event) => {
    event.preventDefault(); // Prevent the default context menu
  };

  useEffect(() => {
    if (!pdfURL || pdfURL === "null" || pdfURL === "undefined" || pdfURL.trim() === "") {
      return;
    }

    // Base64 encode the original URL
    const encodedUrl = btoa(pdfURL);
    setEncodedPdfURL(encodedUrl);
    
    // Check if the PDF is cached and use it directly
    checkAndUseCachedPDF(pdfURL);
  }, [pdfURL]);

  // Add function to check for cached PDF and prepare it for display
  const checkAndUseCachedPDF = async (url) => {
    try {
      const isVerified = await verifyCachedResource(url, "application/pdf");
      if (!isVerified) {
        console.log("PDF not found in cache or invalid content type");
        setIsCached(false);
        return;
      }
      
      const cache = await caches.open("pdf-cache-v1"); // P0: was "appV1"
      const cachedResponse = await cache.match(url);

      if (cachedResponse) {
        console.log("Using cached PDF directly");
        setIsCached(true);
        
        // Instead of just setting a flag, actually use the cached PDF directly
        const blob = await cachedResponse.blob();
        const blobUrl = URL.createObjectURL(blob);
        // Set the encoded blob URL instead of the original URL
        setEncodedPdfURL(btoa(blobUrl));
        
        // Clean up the URL object when the component unmounts
        return () => URL.revokeObjectURL(blobUrl);
      } else {
        console.log("PDF not found in cache");
        setIsCached(false);
      }
    } catch (error) {
      console.error("Error checking PDF cache:", error);
    }
  };

  const decodePdfUrl = (encodedUrl) => {
    if (!encodedUrl) return null;
    // Decode the Base64 encoded URL
    return atob(encodedUrl);
  };

  // Sync with propPageNumber from parent when it changes
  useEffect(() => {
    if (propPageNumber && propPageNumber !== pageNumber) {
      setLocalPageNumber(propPageNumber);
    }
  }, [propPageNumber]);

  // Sync with isPDFCached prop when it changes
  useEffect(() => {
    setIsCached(isPDFCached);
  }, [isPDFCached]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAudioPlay = (voiceFileName) => {
    const audioElements = document.getElementsByTagName("audio");
    for (let i = 0; i < audioElements.length; i++) {
      const audioElement = audioElements[i];
      if (voiceFileName === audioElement.src) {
        audioElement.play();
      } else {
        audioElement.pause();
      }
    }
  };
  const onNextPage = () => {
    const pagesToSkip =
      viewMode === "single" ? 1 : viewMode === "two-page" ? 2 : 3;
    if (pageNumber + pagesToSkip <= numPages) {
      updatePageNumber(pageNumber + pagesToSkip);
    }
  };
  const onPrevPage = () => {
    if (pageNumber > 1) {
      updatePageNumber(pageNumber - 1);
    }
  };

  const onPageNumberChange = (e) => {
    const value = e.target.value;
    const pageNum = parseInt(value, 10);

    if (value === "" || (pageNum > 0 && pageNum <= numPages)) {
      updatePageNumber(pageNum);
    }
  };

  // Handle the view mode change (dropdown)
  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
    if (e.target.value === "double") {
      setScale(0.8);
    }
  };

  // Full screen PDF
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      pdfContainerRef.current.requestFullscreen().catch((err) => {
        console.error(
          `Error attempting to enable fullscreen mode: ${err.message}`
        );
      });
      setIsFullscreen(true);
      if (onFullscreenChange) {
        onFullscreenChange(true);
      }
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
      if (onFullscreenChange) {
        onFullscreenChange(false);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") onNextPage();
      if (event.key === "ArrowLeft") onPrevPage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pageNumber]);

  // Zoom (in & out feature)
  const zoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 3)); // Max zoom level of 3
  };
  const zoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5)); // Min zoom level of 0.5
  };
  const resetZoom = () => {
    setScale(1); // Default zoom level is 1
  };

  const handleSearch = async (term) => {
    setHighlightedMatches([]); // Clear previous highlights
    const pdf = await pdfjs.getDocument(pdfURL).promise;
    const matches = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const textItems = content.items;
      const regex = new RegExp(term, "gi");

      /*       for (const item of textItems) {
        
        if (regex.test(item.str)) {
          matches.push({
            page: i,
            item,
          });
        }
      } */
      for (const item of textItems) {
        // Find all matches of the term within the text content
        let match;
        while ((match = regex.exec(item.str)) !== null) {
          // Calculate the position of the matched word
          const matchStart = match.index;
          const matchLength = match[0].length;

          // Approximate width of the matched word based on the proportion of characters in the whole item
          const wordWidth = (item.width / item.str.length) * matchLength;

          // Approximate X position of the matched word within the text item
          const wordX =
            item.transform[4] + (item.width / item.str.length) * matchStart;

          matches.push({
            page: i,
            item: {
              ...item,
              str: match[0], // Store the exact matched word
              width: wordWidth,
              transform: [
                ...item.transform.slice(0, 4), // Keep the existing scale/rotation values
                wordX, // Adjust the X position
                item.transform[5], // Y position remains the same
              ],
            },
          });
        }
      }
    }

    setSearchResults(matches);
    setTotalMatches(matches.length);
    setCurrentMatch(1);
    highlightMatches(matches);
    setHighlightedMatches(matches);

    // Highlight matches only if there are any
    if (matches.length > 0) {
      highlightMatches(matches);
    }
  };

  // Function to highlight matches in the PDF
  const highlightMatches = (matches) => {
    setHighlightedMatches(matches);
  };

  const onNextResult = () => {
    const matchesOnPage = highlightedMatches.filter(
      (match) => match.page === pageNumber
    );

    // If the current match index is less than the number of matches on this page
    if (currentMatch < matchesOnPage.length) {
      setCurrentMatch(currentMatch + 1); // Move to the next match
    } else {
      // Check if there's a next page
      const nextPage = pageNumber + 1;
      const nextPageMatches = highlightedMatches.filter(
        (match) => match.page === nextPage
      );

      if (nextPageMatches.length > 0) {
        // If there's a next page with matches, navigate to it
        updatePageNumber(nextPage);
        setCurrentMatch(1); // Reset to the first match on the new page
      } else {
        // Wrap around to the first match on the first page (if you want to cycle through)
        updatePageNumber(1);
        const firstMatch = highlightedMatches.find((match) => match.page === 1);
        if (firstMatch) {
          setCurrentMatch(1);
          updatePageNumber(firstMatch.page);
        }
      }
    }
  };

  const onPrevResult = () => {
    const matchesOnPage = highlightedMatches.filter(
      (match) => match.page === pageNumber
    );

    if (currentMatch > 1) {
      setCurrentMatch(currentMatch - 1); // Move to the previous match
    } else {
      // Check if there's a previous page
      const prevPage = pageNumber - 1;
      const prevPageMatches = highlightedMatches.filter(
        (match) => match.page === prevPage
      );

      if (prevPageMatches.length > 0) {
        // If there's a previous page with matches, navigate to it
        updatePageNumber(prevPage);
        setCurrentMatch(prevPageMatches.length); // Reset to the last match on the new page
      } else {
        // Optionally wrap to the last page
        const lastPage = Math.max(
          ...highlightedMatches.map((match) => match.page)
        ); // Get the last page number
        const lastPageMatches = highlightedMatches.filter(
          (match) => match.page === lastPage
        );
        if (lastPageMatches.length > 0) {
          updatePageNumber(lastPage);
          setCurrentMatch(lastPageMatches.length); // Set to the last match index
        }
      }
    }
  };

  // Replace the old handleCacheBook function with this updated version
  const handleCacheBook = async () => {
    // Just delegate to our comprehensive caching function
    await cacheMediaAndPDF();
    
    // Ensure the service worker knows about our book page
    if (bookId && navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        action: "addResourceToCache",
        url: `https://mediasea.net/bookDetails/${bookId}`,
      });
    }
  };

  const renderPages = () => {
    const filteredMatches = highlightedMatches.filter(
      (match) => match.page === pageNumber
    );
    switch (viewMode) {
      case "single":
        return (
          <div className="pdf-single-page">
            <div style={{ position: "relative" }}>
              <Page pageNumber={pageNumber} renderMode="canvas" scale={scale} />

              {filteredMatches.map((match, index) => {
                const { transform } = match.item;
                console.log("Match", match);
                if (!transform || transform.length < 4) {
                  return null; // Avoid rendering if transform is invalid
                }

                const [x, y] = [transform[4], transform[5]];
                const width = match.item.width;
                const height = match.item.height;

                // Adjust for potential coordinate origin differences
                const viewerHeight =
                  document.querySelector(".pdf-pages").clientHeight; // Get viewer height
                const adjustedY = viewerHeight - y - height; // Invert Y if necessary

                // Determine if this match is the current match
                const isCurrentMatch = currentMatch === index + 1; // Check if this is the current match index

                return (
                  <div
                    key={index}
                    className="highlight"
                    style={{
                      position: "absolute",
                      left: x,
                      top: adjustedY,
                      width: width,
                      height: height,
                      backgroundColor: isCurrentMatch
                        ? "rgba(255, 0, 0, 0.5)"
                        : "rgba(255, 255, 0, 0.5)",
                      pointerEvents: "none",
                    }}
                  />
                );
              })}
              {activeTool !== null && (
                <DrawingLayer
                  activeTool={activeTool}
                  pageNumber={pageNumber}
                  annotations={annotations}
                  onUpdateAnnotations={handleUpdateAnnotations}
                />
              )}
            </div>
          </div>
        );
      case "double":
        return (
          <div className="pdf-double-page">
            <div style={{ position: "relative" }}>
              <Page pageNumber={pageNumber} renderMode="canvas" scale={scale} />
              {activeTool !== null && (
                <DrawingLayer
                  activeTool={activeTool}
                  pageNumber={pageNumber}
                  annotations={annotations}
                  onUpdateAnnotations={handleUpdateAnnotations}
                />
              )}
            </div>
            {pageNumber < numPages && (
              <div style={{ position: "relative" }}>
                <Page
                  pageNumber={pageNumber + 1}
                  renderMode="canvas"
                  scale={scale}
                />
                {activeTool !== null && (
                  <DrawingLayer
                    activeTool={activeTool}
                    pageNumber={pageNumber + 1}
                    annotations={annotations}
                    onUpdateAnnotations={handleUpdateAnnotations}
                  />
                )}
              </div>
            )}
          </div>
        );
      case "nextDouble":
        return (
          <div className="pdf-double-page">
            {pageNumber + 1 <= numPages && (
              <div style={{ position: "relative" }}>
                <Page
                  pageNumber={pageNumber + 1}
                  renderMode="canvas"
                  scale={scale}
                />
                {activeTool !== null && (
                  <DrawingLayer
                    activeTool={activeTool}
                    pageNumber={pageNumber + 1}
                    annotations={annotations}
                    onUpdateAnnotations={handleUpdateAnnotations}
                  />
                )}
              </div>
            )}
            {pageNumber + 2 <= numPages && (
              <div style={{ position: "relative" }}>
                <Page
                  pageNumber={pageNumber + 2}
                  renderMode="canvas"
                  scale={scale}
                />
                {activeTool !== null && (
                  <DrawingLayer
                    activeTool={activeTool}
                    pageNumber={pageNumber + 2}
                    annotations={annotations}
                    onUpdateAnnotations={handleUpdateAnnotations}
                  />
                )}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="pdf-single-page">
            <div style={{ position: "relative" }}>
              <Page pageNumber={pageNumber} renderMode="canvas" scale={scale} />
              {highlightedMatches
                .filter((match) => match.page === pageNumber)
                .map((match, index) => {
                  console.log("Match", match);
                  const { transform } = match.item;

                  if (!transform || transform.length < 4) {
                    return null; // Avoid rendering if transform is invalid
                  }
                  const yAdjustment = index * 2;

                  const [x, y] = [transform[4], transform[5]];
                  const width = match.item.width;
                  const height = match.item.height;
                  console.log(
                    `Highlight position - X: ${x}, Y: ${y}, Width: ${width}, Height: ${height}`
                  );
                  return (
                    <div
                      key={index}
                      className="highlight"
                      style={{
                        position: "absolute",
                        left: x,
                        top: y,
                        width: width,
                        height: height,
                        backgroundColor: "rgba(255, 255, 0, 0.5)",
                        pointerEvents: "none",
                      }}
                    />
                  );
                })}
              {activeTool !== null && (
                <DrawingLayer
                  activeTool={activeTool}
                  pageNumber={pageNumber}
                  annotations={annotations}
                  onUpdateAnnotations={handleUpdateAnnotations}
                />
              )}
            </div>
          </div>
        );
    }
  };

  useEffect(() => {
    // Add fullscreenchange event listener
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
        if (onFullscreenChange) {
          onFullscreenChange(false);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isFullscreen, onFullscreenChange]);

  // Function to track PDF loading progress
  const onDocumentLoadProgress = ({ loaded, total }) => {
    const percentage = Math.round((loaded / total) * 100);
    setLoadingProgress(percentage);
  };

  // Function to check if a resource is in the cache and load it if present
  const getPdfFileFromCache = async () => {
    if (!pdfURL || pdfURL === "null" || pdfURL === "undefined" || pdfURL.trim() === "") return null;
    
    try {
      const isVerified = await verifyCachedResource(pdfURL, "application/pdf");
      if (!isVerified) return null;
      
      const cache = await caches.open("pdf-cache-v1"); // P0: was "appV1"
      const cachedResponse = await cache.match(pdfURL);
      
      if (cachedResponse) {
        // Create a URL from the cached response
        const blob = await cachedResponse.blob();
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error("Error retrieving PDF from cache:", error);
    }
    
    return null;
  };

  // Add a useEffect for preloading the cached PDF
  useEffect(() => {
    if (!pdfURL || pdfURL === "null" || pdfURL === "undefined" || pdfURL.trim() === "") {
      setIsLoading(false);
      return;
    }
    
    let blobUrl = null;
    
    const preloadCachedPDF = async () => {
      setIsLoading(true);
      const cachedUrl = await getPdfFileFromCache();
      
      if (cachedUrl) {
        console.log("Preloaded cached PDF", cachedUrl);
        blobUrl = cachedUrl;
        setEncodedPdfURL(btoa(cachedUrl));
        setIsCached(true);
        
        // Still show loading indicator but it will be much faster
        setTimeout(() => setIsLoading(false), 100);
      } else {
        // If not cached, we'll use the original URL
        setEncodedPdfURL(btoa(pdfURL));
        setIsCached(false);
      }
    };
    
    preloadCachedPDF();
    
    // Cleanup function to release blob URLs
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [pdfURL]);

  if (!pdfURL || pdfURL === "null" || pdfURL === "undefined" || pdfURL.trim() === "") {
    return (
      <div className="pdf-no-sample" style={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h5">No Sample Available</Typography>
      </div>
    );
  }

  return (
    <div
      onContextMenu={handleContextMenu}
      className="pdf-container"
      style={{ overflowX: mode == "digital" ? "scroll" : "visible" }}
      ref={pdfContainerRef}
    >
      {/* Background caching indicator */}
      {/* {isCachingInBackground && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          background: 'rgba(0, 0, 0, 0.7)',
          padding: '10px',
          borderRadius: '5px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
        }}>
          <CircularProgress 
            variant="determinate" 
            value={cachingProgress} 
            size={24} 
            sx={{ color: 'white', marginRight: '10px' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.85rem' }}>Saving PDF for offline use</span>
            <span style={{ fontSize: '0.7rem' }}>{Math.round(cachingProgress)}% complete</span>
          </div>
        </div>
      )} */}
      
      {/* Snackbars for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          PDF downloaded successfully for offline reading!
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={snackbarOpenMedia}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpenMedia(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpenMedia(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Media files downloaded successfully for offline use!
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={snackbarOpenMediaAndPdf}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpenMediaAndPdf(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpenMediaAndPdf(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          All content downloaded successfully! Book is now available offline.
        </Alert>
      </Snackbar>
      
    <div className="PDF-container">
      <div className="PDF-header">
        {/* Display Progress Bar */}
        {isDownloading && (
          <div className="progress-bar-container">
            <LinearProgress
              variant="determinate"
              value={progress}
              style={{ width: "100%", height: "10px", marginBottom: "10px" }}
            />
            <span className="progress-text">{progress}%</span>
          </div>
        )}
        {
          /* Version Dropdown */
          !sample && (
            <VersionDropdown
              userId={localStorage.getItem("id")}
              bookId={bookId}
              onLoadVersion={handleLoadVersion}
              annotations={annotations}
            />
          )
        }
        <div className="page-info">
          <SearchComponent
            onSearch={handleSearch}
            onNextResult={onNextResult}
            onPrevResult={onPrevResult}
            totalMatches={totalMatches}
            currentMatch={currentMatch}
            setSearchTerm={setSearchTerm}
          />
          <Tooltip title="Zoom In">
            <ZoomInIcon onClick={zoomIn} />
          </Tooltip>

          <Tooltip title="Reset Zoom">
            <RestartAltIcon onClick={resetZoom} />
          </Tooltip>

          <Tooltip title="Zoom Out">
            <ZoomOutIcon onClick={zoomOut} />
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            {isFullscreen ? (
              <FullscreenExitIcon
                onClick={toggleFullscreen}
                className="fullscreen-icon"
              />
            ) : (
              <FullscreenIcon
                onClick={toggleFullscreen}
                className="fullscreen-icon"
              />
            )}
          </Tooltip>

          {/* Hide download button entirely if everything is cached */}
          {!sample && !isDownloading && !isMediaCaching && 
           !((isCached && isMediaCached) || 
             (isCached && !hasMedia) || 
             (!hasPDF && isMediaCached)) && (
            <Tooltip title={downloadButtonState.text}>
              <span>
                <IconButton 
                  onClick={handleMenuOpen} 
                  sx={{ color: "white" }}
                  disabled={downloadButtonState.disabled}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          {/* Show offline indicator if everything is cached */}
          {!sample && !isDownloading && !isMediaCaching && 
           ((isCached && isMediaCached) || 
            (isCached && !hasMedia) || 
            (!hasPDF && isMediaCached)) && (
            <Tooltip title="Available offline">
              <span>
                <IconButton 
                  sx={{ color: "white", opacity: 0.8 }}
                  disabled={true}
                >
                  <OfflinePinIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          {/* Download progress indicator */}
          {(isDownloading || isMediaCaching) && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: 'white',
              marginLeft: '10px'
            }}>
              <CircularProgress 
                size={24} 
                thickness={5} 
                variant="determinate" 
                value={isDownloading ? progress : mediaCachingProgress}
                sx={{ color: 'white', marginRight: '8px' }}
              />
              <span style={{ fontSize: '0.85rem' }}>
                {isDownloading ? 
                  `Caching PDF: ${progress}%` : 
                  `Caching ${cachingFileName ? cachingFileName : 'media'}: ${mediaCachingProgress}%`
                }
              </span>
            </div>
          )}

          {/* Download menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            
            {hasMedia && !isMediaCached && (
              <MenuItem
                onClick={() => {
                  cacheMediaFiles();
                  handleMenuClose();
                }}
              >
                Download Media Files
              </MenuItem>
            )}
            
            {((hasPDF && !isCached) || (hasMedia && !isMediaCached)) && (
              <MenuItem
                onClick={() => {
                  cacheMediaAndPDF();
                  handleMenuClose();
                }}
              >
                Download All Content
              </MenuItem>
            )}
          </Menu>
          
          {isFullscreen && !sample && fullscreenSidebar && book && (
            <div className="left-sidebar">
              <DigitalSidebar 
                book={book} 
                setPageNumber={updatePageNumber}
                currentPage={pageNumber}
              />
            </div>
          )}

          {mode === "digital" && (
            <>
              <label htmlFor="viewMode">View Mode: </label>
              <select
                id="viewMode"
                value={viewMode}
                onChange={handleViewModeChange}
              >
                <option value="single">{`Page ${pageNumber}`}</option>
                <option value="double">{`Page ${pageNumber}-${
                  pageNumber + 1 > numPages ? numPages : pageNumber + 1
                }`}</option>
                <option value="nextDouble">{`Page ${pageNumber + 1}-${
                  pageNumber + 2 > numPages ? numPages : pageNumber + 2
                }`}</option>
              </select>
            </>
          )}
          <span style={{ marginLeft: "10px" }}>
            <button
              className="prev-button"
              onClick={onPrevPage}
              disabled={pageNumber <= 1}
            >
              &#x2039; {/* Left arrow */}
            </button>
            Page{" "}
            <input
              type="number"
              value={pageNumber}
              onChange={onPageNumberChange}
              min="1"
              max={numPages}
              className="page-number-input"
            />{" "}
            of {numPages}
            <button
              className="next-button"
              onClick={onNextPage}
              disabled={pageNumber >= numPages}
            >
              &#x203A; {/* Right arrow */}
            </button>
          </span>
        </div>
      </div>

      <Document
        className="PDF-Page"
        file={decodePdfUrl(encodedPdfURL)}
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages);
          setIsLoading(false);
        }}
        onLoadError={(error) => {
          setIsLoading(false);
          console.error("Error loading PDF:", error);
          
          // If loading from URL fails and we have it cached, try to use the cached version
          getPdfFileFromCache().then(cachedUrl => {
            if (cachedUrl) {
              console.log("Falling back to cached PDF");
              setEncodedPdfURL(btoa(cachedUrl));
            }
          });
        }}
        loading={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <div style={{ position: "relative", marginBottom: "15px" }}>
              <CircularProgress size={60} thickness={4} />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography variant="body2" component="div" style={{ fontWeight: "bold" }}>
                  {loadingProgress}%
                </Typography>
              </div>
            </div>
            <Typography variant="h6">
              {isCached ? "Preparing PDF..." : "Loading PDF..."}
            </Typography>
          </div>
        }
        onLoadProgress={onDocumentLoadProgress}
        cMapUrl="https://unpkg.com/pdfjs-dist@3.11.174/cmaps/"
        cMapPacked={true}
      >
        <div className="pdf-pages">{renderPages()}</div>
      </Document>
      <div className="pdf-navigation"></div>

      {!sample && (
        <ActionBar setActiveTool={setActiveTool} activeTool={activeTool} />)
      }
    </div>
    </div>
  );
};

export default PDFViewer;
